import { isArray, isEmpty, isFunction, isObject, isSet, objectValues } from '@/global/utils/utils'
import { api } from '@/global/services/api'
import gridView from '@/global/components/grid/GridView.vue'
import GenericGridBlock from '@/global/components/grid/GenericGridBlock.vue'
import dayByDayConfig from '@/modules/base-module/reports/overview/configs/reports/dayByDay'
import tripsConfig from '@/modules/base-module/reports/overview/configs/reports/trips'
import vehicleSummaryConfig from '@/modules/base-module/reports/overview/configs/reports/vehicleSummary'
import stopsConfig from '@/modules/base-module/reports/overview/configs/reports/stops'
import CellIcon from '@/global/components/table/components/TableCellIcon.vue'
import roadOverspeedConfig from '@/modules/base-module/reports/overview/configs/reports/roadOverspeed'
import overspeedConfig from '@/modules/base-module/reports/overview/configs/reports/overspeed'
import lowVoltageConfig from '@/modules/base-module/reports/overview/configs/reports/lowVoltage'
import geofenceConfig from '@/modules/base-module/reports/overview/configs/reports/geofence'
import idleVehOpConfig from '@/modules/base-module/reports/overview/configs/reports/idleVehOp'
import temperatureConfig from '@/modules/base-module/reports/overview/configs/reports/temperature'
import expensesConfig from '@/modules/base-module/reports/overview/configs/reports/expenses'
import passThLocationConfig from '@/modules/base-module/reports/overview/configs/reports/passThLocation'
import vehicleSensorsConfig from '@/modules/base-module/reports/overview/configs/reports/vehicleSensors'
import machineOperationConfig from '@/modules/base-module/reports/overview/configs/reports/machineOperation'
import businessPrivateDriveConfig from '@/modules/base-module/reports/overview/configs/reports/businessPrivateDrive'
import vehicleAvailabilityConfig from '@/modules/base-module/reports/overview/configs/reports/vehicleAvailability'
import driversDayByDayConfig from '@/modules/base-module/reports/overview/configs/reports/driversDayByDay'
import driversSummaryConfig from '@/modules/base-module/reports/overview/configs/reports/driversSummary'
import activityHistoryConfig from '@/modules/base-module/reports/overview/configs/reports/activityHistory'
import vehicleIncidentViolationsConfig from '@/modules/base-module/reports/overview/configs/reports/vehicleIncidentViolations'
import vehicleIncidentDamagesConfig from '@/modules/base-module/reports/overview/configs/reports/vehicleIncidentDamages'
import driverTripsConfig from '@/modules/base-module/reports/overview/configs/reports/driverTrips'
import driversStopsConfig from '@/modules/base-module/reports/overview/configs/reports/driversStops'
import driversViolationsConfig from '@/modules/base-module/reports/overview/configs/reports/driversViolations'
import driversDamagesConfig from '@/modules/base-module/reports/overview/configs/reports/driversDamages'
import { notify } from '@/global/services/notification/notificationBus'
import ExportButton from '@/modules/base-module/reports/overview/components/ExportButton.vue'
import SensorActivationsExpandTable from '@/modules/base-module/reports/overview/components/SensorActivationsExpandTable.vue'
import { dateService, FORMATS } from '@/global/services/date'
import helperMixin from '@/global/mixins/helperMixin'

const REPORT_CATEGORY_VEHICLES = 'vehicles'
const REPORT_CATEGORY_SENSORS = 'sensors'
const EXPORT_FILE_MIME_TYPES = {
  csv: 'text/csv;charset=utf-8;',
  pdf: 'application/pdf',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
}

export default {
  mixins: [
    helperMixin
  ],

  data () {
    return {
      showReportOptionsDialog: false,
      showLoader: false
    }
  },

  computed: {
    reportName () {
      return this.getReportBaseData?.name || '-'
    },

    // reportParams is provided by the host component (from the reportOverview store),
    // populated when the report was generated in ReportOptionsDialog.vue
    isMachine () {
      return this.reportParams?.isSelectedVehicleTypeMachine === 1
    },

    // after a page refresh the report options store is empty, so there is no report to preview
    hasSelectedReport () {
      return !isEmpty(this.getReportBaseData?.name)
    },

    reportAdditionalButtons () {
      return [
        {
          component: ExportButton,
          mobileRow: 0,
          onConfirm: (selectedFormat) => this.onExport(selectedFormat)
        },
        {
          label: this.$t('satellite-tracking/report.choose_parameters'),
          prependIcon: 'mdi-cog',
          color: '#65AD45',
          style: {
            color: 'white',
            width: '100%'
          },
          prependIconStyle: {
            fontSize: '17px'
          },
          mobileRow: 1,
          mobileFullWidth: true,
          click: () => this.openReportOptionsDialog()
        }
      ]
    },

    breadcrumbs () {
      return {
        items: [
          {
            text: this.$t('base.reports')
          },
          {
            text: this.$t(this.reportName || '-'),
            bolded: true
          }
        ]
      }
    },

    reportRoute () {
      return [REPORT_CATEGORY_VEHICLES, REPORT_CATEGORY_SENSORS].includes(this.reportCategory)
        ? 'vehicle-report'
        : 'driver-report-new'
    },

    reportRouteType () {
      return [REPORT_CATEGORY_VEHICLES, REPORT_CATEGORY_SENSORS].includes(this.reportCategory)
        ? 'post'
        : 'get'
    },

    defaultPaginationConfig () {
      return {
        perPageOptions: [50, 100],
        perPage: 50
      }
    },

    defaultTableGlobalConfig () {
      return {
        showLoader: this.showLoader,
        gridConfig: {
          component: gridView,
          blockConfig: {
            component: GenericGridBlock,
            visibleFieldsCount: 4
          },
          useBorder: false
        },
        title_and_back_config: {
          title: this.$t('base.back'),
          showBackButton: {
            uiViewName: 'ReportsOverview'
          }
        },
        search_and_buttons_config: {
          additional_buttons: this.reportAdditionalButtons
        },
        internalPagination: {
          enable: true
        },
        allowDataFetch: false,
        filters_and_columns_visibility_config: {
          showColumnsVisibilityButton: false,
          showFiltersButton: false,
          showRefreshButton: false
        },
        mobileConfig: {
          title: this.$t(this.reportName),
          showNew: false,
          showFilters: false,
          showSearch: false,
          additionalButtons: this.reportAdditionalButtons
        }
      }
    },

    defaultTableConfig () {
      return {
        summaryConfig: {
          cellClass: 'report-summary-cell'
        },
        mapSummaryDataByColumns: false
      }
    },

    dayByDayReportHeaders () {
      const type = this.isMachine ? 'machine' : 'vehicle'

      const headers = [
        ...dayByDayConfig.base,
        ...dayByDayConfig[type]
      ].map(([label, key]) => this.header(label, key))

      return this.withDefaults(headers)
    },

    tripsReportHeaders () {
      let headers = this.isMachine ? [...tripsConfig.machineHeaders] : [...tripsConfig.vehicleHeaders]
      headers = headers.map(([label, key]) => this.header(label, key))
      return this.withDefaults(headers)
    },

    computedTabKey () {
      return isSet(this.reportData) && isObject(this.reportData) && !this.activeTabKey
        ? parseInt(Object.keys(this.reportData)[0], 10)
        : this.activeTabKey
    },

    computedTemperatureReportTabKey () {
      return isSet(this.reportData?.groupedData) && isObject(this.reportData.groupedData) && !this.activeTabKey
        ? Object.keys(this.reportData.groupedData)[0]
        : this.activeTabKey
    },

    vehicleSummaryReportHeaders () {
      const type = this.isMachine ? 'machine' : 'vehicle'

      const headers = [
        ...vehicleSummaryConfig.base,
        ...vehicleSummaryConfig[type]
      ].map(([label, key]) => this.header(label, key))

      return this.withDefaults(headers)
    },

    stopsReportHeaders () {
      const headers = stopsConfig.headers.map(([label, key]) => this.header(label, key))
      const resultHeaders = this.withDefaults(headers)

      resultHeaders.push({
        label: this.$t('satellite-tracking/report.location_name'),
        key: 'address',
        component: this.reverseGeocodePreviewComponent,
        always_visible: true,
        style: {
          whiteSpace: 'nowrap'
        },
        batchReverseGeocode: this.batchReverseGeocode,
        batchGeoreverseResult: () => this.batchGeoreverseResult,
        setCoordinatesInQueue: (event) => this.setCoordinatesInQueue(event),
        params: (event) => {
          return {
            lat: event?.data?.lat,
            lng: event?.data?.lng
          }
        }
      })

      resultHeaders.push({
        label: this.$t('satellite-tracking/report.resting_time'),
        key: 'duration',
        style: {
          whiteSpace: 'nowrap'
        },
        always_visible: true
      })

      resultHeaders.push({
        label: this.$t('satellite-tracking/report.map_routes'),
        key: 'mapRoutes',
        style: {
          textAlign: 'center',
          whiteSpace: 'nowrap'
        },
        component: CellIcon,
        disableDefaultClick: true,
        always_visible: true,
        onClick: (event) => this.showLocationOnMap(event),
        src: '/img/icons/show-on-map-icon.svg'
      })

      return this.filterByColumnConfig(resultHeaders)
    },

    overspeedReportHeaders () {
      const headers = overspeedConfig.headers.map(([label, key]) => this.header(label, key))
      const resultHeaders = this.withDefaults(headers)

      resultHeaders.push({
        label: this.$t('satellite-tracking/report.address'),
        key: 'address',
        component: this.reverseGeocodePreviewComponent,
        always_visible: true,
        style: {
          whiteSpace: 'nowrap'
        },
        batchReverseGeocode: this.batchReverseGeocode,
        batchGeoreverseResult: () => this.batchGeoreverseResult,
        setCoordinatesInQueue: (event) => this.setCoordinatesInQueue(event),
        params: (event) => {
          return {
            lat: event?.data?.lat,
            lng: event?.data?.lng
          }
        }
      })

      resultHeaders.push({
        label: this.$t('satellite-tracking/report.maxSpeed'),
        key: 'maxSpeed',
        style: {
          whiteSpace: 'nowrap'
        },
        always_visible: true
      })

      resultHeaders.push({
        label: this.$t('satellite-tracking/report.map_routes'),
        key: 'mapRoutes',
        style: {
          textAlign: 'center',
          whiteSpace: 'nowrap'
        },
        component: CellIcon,
        disableDefaultClick: true,
        always_visible: true,
        onClick: (event) => this.showLocationOnMap(event),
        src: '/img/icons/show-on-map-icon.svg'
      })

      return this.filterByColumnConfig(resultHeaders)
    },

    roadOverspeedReportHeaders () {
      const headers = roadOverspeedConfig.headers.map(([label, key]) => this.header(label, key))
      const resultHeaders = this.withDefaults(headers)

      resultHeaders.push({
        label: this.$t('satellite-tracking/report.address'),
        key: 'address',
        component: this.reverseGeocodePreviewComponent,
        always_visible: true,
        style: {
          whiteSpace: 'nowrap'
        },
        batchReverseGeocode: this.batchReverseGeocode,
        batchGeoreverseResult: () => this.batchGeoreverseResult,
        setCoordinatesInQueue: (event) => this.setCoordinatesInQueue(event),
        params: (event) => {
          return {
            lat: event?.data?.lat,
            lng: event?.data?.lng
          }
        }
      })

      resultHeaders.push({
        label: this.$t('satellite-tracking/report.speed'),
        key: 'speed',
        style: {
          whiteSpace: 'nowrap'
        },
        always_visible: true
      })

      resultHeaders.push({
        label: this.$t('satellite-tracking/report.speed_limit'),
        key: 'speedLimit',
        style: {
          whiteSpace: 'nowrap'
        },
        always_visible: true
      })

      resultHeaders.push({
        label: this.$t('satellite-tracking/report.map_routes'),
        key: 'mapRoutes',
        style: {
          textAlign: 'center',
          whiteSpace: 'nowrap'
        },
        component: CellIcon,
        disableDefaultClick: true,
        always_visible: true,
        onClick: (event) => this.showLocationOnMap(event),
        src: '/img/icons/show-on-map-icon.svg'
      })

      return this.filterByColumnConfig(resultHeaders)
    },

    lowVoltageReportHeaders () {
      return this.withDefaults(lowVoltageConfig.headers.map(([label, key]) => this.header(label, key)))
    },

    geofenceReportHeaders () {
      return this.withDefaults(geofenceConfig.headers.map(([label, key]) => this.header(label, key)))
    },

    idleVehOpReportHeaders () {
      return this.withDefaults(idleVehOpConfig.headers.map(([label, key]) => this.header(label, key)))
    },

    temperatureReportHeaders () {
      return this.withDefaults(temperatureConfig.headers.map(([label, key]) => this.header(label, key)))
    },

    expensesReportHeaders () {
      return this.withDefaults(expensesConfig.headers.map(([label, key]) => this.header(label, key)))
    },

    passThLocationReportHeaders () {
      const headers = passThLocationConfig.headers.map(([label, key]) => this.header(label, key))
      const resultHeaders = this.withDefaults(headers)

      resultHeaders.push({
        label: this.$t('satellite-tracking/report.map_routes'),
        key: 'mapRoutes',
        style: {
          textAlign: 'center',
          whiteSpace: 'nowrap'
        },
        component: CellIcon,
        disableDefaultClick: true,
        always_visible: true,
        onClick: (event) => this.showRouteOverview(event),
        src: '/img/icons/show-on-map-icon.svg'
      })

      return this.filterByColumnConfig(resultHeaders)
    },

    vehicleSensorsReportHeaders () {
      const isTimeFormat = this.selectedSensor?.display_format === 'time'
      let headers = this.selectedSensor?.type !== 'digital'
        ? vehicleSensorsConfig.nonDigitalHeaders
        : (isTimeFormat ? vehicleSensorsConfig.timeVehicleHeaders : vehicleSensorsConfig.headers)
      if (isTimeFormat) {
        headers = [
          ...headers,
          ...vehicleSensorsConfig.timeHeaders
        ]
      }
      else {
        headers = [
          ...headers,
          ...vehicleSensorsConfig.distanceHeaders
        ]
      }
      headers = [
        ...headers,
        ...vehicleSensorsConfig.defaultLastHeaders
      ]

      return this.withDefaults(headers.map(([label, key]) => this.header(label, key)))
    },

    machineOperationReportHeaders () {
      return this.withDefaults(machineOperationConfig.headers.map(([label, key]) => this.header(label, key)))
    },

    businessPrivateDriveReportHeaders () {
      return this.withDefaults(businessPrivateDriveConfig.headers.map(([label, key]) => this.header(label, key)))
    },

    vehicleAvailabilityReportHeaders () {
      return this.withDefaults(vehicleAvailabilityConfig.headers.map(([label, key]) => this.header(label, key)))
    },

    driversDayByDayReportHeaders () {
      return this.withDefaults(driversDayByDayConfig.headers.map(([label, key]) => this.header(label, key)))
    },

    driversSummaryReportHeaders () {
      return this.withDefaults(driversSummaryConfig.headers.map(([label, key]) => this.header(label, key)))
    },

    vehicleActivityHistoryReportHeaders () {
      return this.withDefaults(activityHistoryConfig.headers.map(([label, key]) => this.header(label, key)))
    },

    vehicleIncidentViolationsReportHeaders () {
      return this.withDefaults(vehicleIncidentViolationsConfig.headers.map(([label, key]) => this.header(label, key)))
    },

    vehicleIncidentDamagesReportHeaders () {
      return this.withDefaults(vehicleIncidentDamagesConfig.headers.map(([label, key]) => this.header(label, key)))
    },

    driversTripsReportHeaders () {
      return this.withDefaults(driverTripsConfig.headers.map(([label, key]) => this.header(label, key)))
    },

    driversStopsReportHeaders () {
      const headers = driversStopsConfig.headers.map(([label, key]) => this.header(label, key))
      const resultHeaders = this.withDefaults(headers)

      resultHeaders.push({
        label: this.$t('satellite-tracking/report.location_name'),
        key: 'address',
        component: this.reverseGeocodePreviewComponent,
        always_visible: true,
        style: {
          whiteSpace: 'nowrap'
        },
        batchReverseGeocode: this.batchReverseGeocode,
        batchGeoreverseResult: () => this.batchGeoreverseResult,
        setCoordinatesInQueue: (event) => this.setCoordinatesInQueue(event),
        params: (event) => {
          return {
            lat: event?.data?.lat,
            lng: event?.data?.lng
          }
        }
      })

      resultHeaders.push({
        label: this.$t('satellite-tracking/report.resting_time'),
        key: 'duration',
        style: {
          whiteSpace: 'nowrap'
        },
        always_visible: true
      })

      resultHeaders.push({
        label: this.$t('satellite-tracking/report.map_routes'),
        key: 'mapRoutes',
        style: {
          textAlign: 'center',
          whiteSpace: 'nowrap'
        },
        component: CellIcon,
        disableDefaultClick: true,
        always_visible: true,
        onClick: (event) => this.showLocationOnMap(event),
        src: '/img/icons/show-on-map-icon.svg'
      })

      return this.filterByColumnConfig(resultHeaders)
    },

    driversViolationsReportHeaders () {
      return this.withDefaults(driversViolationsConfig.headers.map(([label, key]) => this.header(label, key)))
    },

    driversDamagesReportHeaders () {
      return this.withDefaults(driversDamagesConfig.headers.map(([label, key]) => this.header(label, key)))
    },

    activeColumnKeys () {
      const config = this.getReportColumnConfig
      if (!config || typeof config !== 'object') return null
      const keys = Object.keys(config)
      return keys.length ? new Set(keys) : null
    },

    filtersReportDataByTab () {
      let reportData = []
      if (this.isMobile && isObject(this.reportData)) {
        reportData = objectValues(this.reportData).reduce(
          (acc, arr) => acc.concat(arr),
          []
        )
      }
      else {
        reportData = this.filterTabDataByKey(this.reportData)
      }

      return reportData
    }
  },

  methods: {
    showLocationOnMap (event) {
      const { lat, lng } = event?.rowData || {}

      if (!lat || !lng) {
        return
      }

      this.$set(this.chooseLocationDialogConfig, 'markers', {
        lat,
        lng
      })
      this.$set(this.chooseLocationDialogConfig, 'title', this.$t('base.location_preview'))
      this.$set(this.chooseLocationDialogConfig, 'show', true)
    },

    async showRouteOverview (event) {
      try {
        const item = event?.rowData
        const payload = {
          vehicle_id: item.vehicleId,
          from: item.startTimeUTC,
          timezone: item.timezone,
          startTime: item.startTime,
          endTime: item.endTime,
          latitude: item.latitude,
          longitude: item.longitude
        }

        const data = await api('satellite-tracking').post('get-trip-by-dateTime-and-vehicleId-parameters', payload)

        this.$set(this.routeOverviewDialog, 'vehicleId', item.vehicleId || null)
        this.$set(this.routeOverviewDialog, 'routeData', data)
        this.$set(this.routeOverviewDialog, 'circleMarker', [item.latitude, item.longitude])
        this.$set(this.routeOverviewDialog, 'tooltip', item?.retentionTime)
        this.$set(this.routeOverviewDialog, 'show', true)
      }
      catch (exception) {
        console.error(exception)
      }
    },

    closeRouteOverviewDialog () {
      this.$set(this.routeOverviewDialog, 'vehicleId', null)
      this.$set(this.routeOverviewDialog, 'routeData', null)
      this.$set(this.routeOverviewDialog, 'circleMarker', null)
      this.$set(this.routeOverviewDialog, 'circleMarkerLabel', null)
      this.$set(this.routeOverviewDialog, 'extraMarkers', null)
      this.$set(this.routeOverviewDialog, 'highlightRange', null)
      this.$set(this.routeOverviewDialog, 'tooltip', null)
      this.$set(this.routeOverviewDialog, 'show', false)
    },

    async showActivationRouteOverview (activation) {
      try {
        const payload = {
          vehicle_id: activation.vehicleId,
          from: activation.activatedAtUTC,
          timezone: activation.timezone,
          startTime: activation.activatedAt,
          endTime: activation.deactivatedAt,
          latitude: activation.latitude,
          longitude: activation.longitude
        }

        const data = await api('satellite-tracking').post('get-trip-by-dateTime-and-vehicleId-parameters', payload)

        // Positions can be missing when the vehicle has no position recorded before the activation
        const hasStartPosition = isSet(activation.latitude) && isSet(activation.longitude)
        const hasEndPosition = isSet(activation.endLatitude) && isSet(activation.endLongitude)

        this.$set(this.routeOverviewDialog, 'vehicleId', activation.vehicleId || null)
        this.$set(this.routeOverviewDialog, 'routeData', data)
        this.$set(this.routeOverviewDialog, 'circleMarker', hasStartPosition ? [activation.latitude, activation.longitude] : null)
        this.$set(this.routeOverviewDialog, 'circleMarkerLabel', 'satellite-tracking/report.sensor_activated')
        this.$set(this.routeOverviewDialog, 'tooltip', activation.activatedAt)
        this.$set(this.routeOverviewDialog, 'extraMarkers', hasEndPosition
          ? [{
              coordinates: [activation.endLatitude, activation.endLongitude],
              tooltip: `<b>${this.$t('satellite-tracking/report.sensor_deactivated')}:</b> ${activation.deactivatedAt}`
            }]
          : [])
        this.$set(this.routeOverviewDialog, 'highlightRange', {
          start: activation.activatedAtISO,
          end: activation.deactivatedAtISO
        })
        this.$set(this.routeOverviewDialog, 'show', true)
      }
      catch (exception) {
        console.error(exception)
      }
    },

    openReportOptionsDialog () {
      this.showReportOptionsDialog = true
    },

    async onExport (selectedFormat) {
      try {
        this.showLoader = true
        if (!selectedFormat || !this.reportRoute || !this.reportParams || isEmpty(this.reportParams) || !this.reportType) {
          return
        }

        const params = this.buildTabParams(this.computedTabKey)
        const dateFormat = this.reportDetails?.useDateTime
          ? FORMATS.datetimeShort
          : FORMATS.date

        const normalizeExportDate = (date) => {
          if (!date) {
            return null
          }

          return typeof date === 'string' && date.includes('T')
            ? dateService.formatIsoString(date)
            : dateService.formatIsoString(date, dateFormat)
        }

        const exportData = await api('satellite-tracking').post(`${this.reportRoute}/export`, {
          ...params,
          dateFrom: this.reportDetails?.useDateTime ? normalizeExportDate(params.dateFrom) : params.dateFrom,
          dateTo: this.reportDetails?.useDateTime ? normalizeExportDate(params.dateTo) : params.dateTo,
          reportType: this.reportType,
          fileFormat: selectedFormat
        }, {
          responseType: 'arraybuffer'
        })

        await this.downloadExportResult(exportData, selectedFormat)
      }
      catch (exception) {
        console.error(exception)
      }
      finally {
        this.showLoader = false
      }
    },

    async downloadExportResult (exportData, selectedFormat) {
      if (!exportData) {
        return
      }

      const mimeType = EXPORT_FILE_MIME_TYPES[selectedFormat] || 'application/octet-stream'
      const fileName = `${this.$t(this.reportName)}_${dateService.now(FORMATS.filename)}.${selectedFormat || 'bin'}`
      const byteArray = exportData instanceof Blob
        ? new Uint8Array(await exportData.arrayBuffer())
        : exportData instanceof ArrayBuffer
          ? new Uint8Array(exportData)
          : ArrayBuffer.isView(exportData)
            ? new Uint8Array(exportData.buffer, exportData.byteOffset, exportData.byteLength)
            : new Uint8Array(new TextEncoder().encode(String(exportData)))

      const blob = new Blob([byteArray], { type: mimeType })
      const objectURL = window.URL.createObjectURL(blob)
      const link = window.document.createElement('a')

      link.href = objectURL
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      setTimeout(() => window.URL.revokeObjectURL(objectURL), 0)
    },

    header (label, key) {
      return {
        label: this.$t(label),
        key
      }
    },

    filterByColumnConfig (headers) {
      if (!this.activeColumnKeys) return headers
      return headers.filter(h => this.activeColumnKeys.has(h.key))
    },

    withDefaults (headers) {
      const mapped = headers.map(h => ({
        ...h,
        style: { whiteSpace: 'nowrap' },
        always_visible: true
      }))
      return this.filterByColumnConfig(mapped)
    },

    /**
     * Get headers for report data table by report name
     * @return {*|null}
     */
    getReportOverviewTableConfig () {
      // no report selected (e.g. page refreshed inside a report) - nothing to configure and nothing to warn about
      if (!this.hasSelectedReport) {
        return null
      }

      const mapping = {
        'satellite-tracking/report.trips_report': () => this.getTripsReportTableConfig(),
        'satellite-tracking/report.day_by_day_report': () => this.getDayByDayReportTableConfig(),
        'satellite-tracking/report.summary_report': () => this.getVehicleSummaryReportTableConfig(),
        'satellite-tracking/report.stops_report': () => this.getStopsReportTableConfig(),
        'satellite-tracking/report.overspeed_report': () => this.getOverspeedReportTableConfig(),
        'satellite-tracking/report.road_overspeed': () => this.getRoadOverspeedReportTableConfig(),
        'satellite-tracking/report.low_voltage_level': () => this.getLowVoltageReportTableConfig(),
        'satellite-tracking/report.geofence_report': () => this.getGeofenceReportTableConfig(),
        'satellite-tracking/report.idle_vehicle_operation': () => this.getIdleVehOpReportTableConfig(),
        'satellite-tracking/report.temperature_report': () => this.getTemperatureReportTableConfig(),
        'satellite-tracking/report.expenses_report': () => this.getExpensesReportTableConfig(),
        'satellite-tracking/report.through_location_report': () => this.getPassingThroughLocationReportTableConfig(),
        'satellite-tracking/report.vehicle_sensors': () => this.getVehicleSensorsReportTableConfig(),
        'satellite-tracking/report.machine_operation': () => this.getMachineOperationReportTableConfig(),
        'satellite-tracking/report.business_private_drive': () => this.getBusinessPrivateDriveReportTableConfig(),
        'satellite-tracking/report.availability_status_report': () => this.getVehicleAvailabilityReportTableConfig(),
        'satellite-tracking/report.drivers_by_day_report': () => this.getDriversByDayReportTableConfig(),
        'satellite-tracking/report.drivers_summary_report': () => this.getDriversSummaryReportTableConfig(),
        'satellite-tracking/report.activity_history_report': () => this.getVehicleActivityHistoryReportTableConfig(),
        'satellite-tracking/report.violations_report': () => this.getVehicleIncidentViolationsReportTableConfig(),
        'satellite-tracking/report.damage_report': () => this.getVehicleIncidentDamagesReportTableConfig(),
        'satellite-tracking/report.drivers_trips_report': () => this.getDriversTripsReportTableConfig(),
        'satellite-tracking/report.drivers_stops_report': () => this.getDriversStopsReportTableConfig(),
        'satellite-tracking/report.drivers_violations_report': () => this.getDriversViolationsReportTableConfig(),
        'satellite-tracking/report.drivers_damage_report': () => this.getDriversDamagesReportTableConfig()
      }

      const handler = mapping?.[this.reportName]
      if (!handler) {
        notify(this.$t('base/reports.undefined_table_config').replace(':reportName', '"' + this.$t(this.reportName) + '"'))
        return null
      }
      return handler()
    },

    // FILTER TAB DATA BY KEY
    filterTabDataByKey (data, wrapResultInArray = false) {
      if (this.isTemperatureReport) {
        return isSet(data?.groupedData?.[this.computedTemperatureReportTabKey]) && isObject(data.groupedData[this.computedTemperatureReportTabKey])
          ? Object.values(data.groupedData[this.computedTemperatureReportTabKey])
          : []
      }

      return isSet(data?.[this.computedTabKey]) && isArray(data[this.computedTabKey])
        ? (wrapResultInArray ? [data[this.computedTabKey]] : data[this.computedTabKey])
        : []
    },

    // ON TAB CHANGE EVENT
    // all report data is fetched upfront, so switching tabs only needs to filter what's already loaded
    onTabChange (event) {
      const { tabKey, setTableData, setSummaryData } = event || {}
      this.setActiveTabKey(tabKey)
      if (!isSet(tabKey)) {
        return
      }

      if (isFunction(setTableData)) {
        setTableData(this.filterTabDataByKey(this.reportData))
      }

      // temperature report has no separate summary data per tab
      if (this.isTemperatureReport) {
        return
      }

      if (isFunction(setSummaryData)) {
        setSummaryData(this.filterTabDataByKey(this.reportSummaryData, true))
      }
    },

    buildTabParams (tabKey) {
      switch (this.reportName) {
        case 'satellite-tracking/report.day_by_day_report':
          return {
            ...this.reportParams,
            vehicleIds: [tabKey]
          }
        case 'satellite-tracking/report.idle_vehicle_operation':
          return {
            ...this.reportParams,
            vehicleIds: [tabKey]
          }
        case 'satellite-tracking/report.drivers_by_day_report':
          return {
            ...this.reportParams,
            personId: [tabKey]
          }
        case 'satellite-tracking/report.business_private_drive':
          return {
            ...this.reportParams,
            vehicleIds: [tabKey]
          }
        default:
          return this.reportParams
      }
    },

    getTripsReportTableConfig () {
      return {
        tableConfig: {
          key: 'trips_report',
          tableData: this.reportData,
          summaryData: [this.reportSummaryData],
          mobileConfig: {
            title: this.$t('base/alarms.tracking'),
            showNew: false
          },
          ...this.defaultTableGlobalConfig,
          tableConfig: {
            ...this.defaultTableConfig,
            headers: this.tripsReportHeaders,
            paginationConfig: this.defaultPaginationConfig
          }
        },
        breadcrumbs: this.breadcrumbs
      }
    },

    getDayByDayReportTableConfig () {
      return {
        tableConfig: {
          key: 'day_by_day_report',
          tableData: this.filtersReportDataByTab,
          summaryData: this.filterTabDataByKey(this.reportSummaryData, true),
          showLoader: this.showLoader,
          ...this.defaultTableGlobalConfig,
          underFiltersCustomComponents: [
            {
              component: this.tableTabsComponent,
              onTabChange: (event) => this.onTabChange(event)
            }
          ],
          tableConfig: {
            ...this.defaultTableConfig,
            headers: this.dayByDayReportHeaders,
            paginationConfig: this.defaultPaginationConfig
          }
        },
        breadcrumbs: this.breadcrumbs
      }
    },

    getVehicleSummaryReportTableConfig () {
      return {
        tableConfig: {
          key: 'vehicles_summary_report',
          tableData: this.reportData,
          summaryData: [this.reportSummaryData],
          ...this.defaultTableGlobalConfig,
          tableConfig: {
            ...this.defaultTableConfig,
            headers: this.vehicleSummaryReportHeaders,
            paginationConfig: this.defaultPaginationConfig
          }
        },
        breadcrumbs: this.breadcrumbs
      }
    },

    getStopsReportTableConfig () {
      const mapRoutesVisible = !this.activeColumnKeys || this.activeColumnKeys.has('mapRoutes')
      return {
        tableConfig: {
          key: 'stops_report',
          tableData: this.reportData,
          summaryData: [
            [
              ...this.reportSummaryData,
              ...(mapRoutesVisible ? [' '] : [])
            ]
          ],
          ...this.defaultTableGlobalConfig,
          tableConfig: {
            ...this.defaultTableConfig,
            cellsConfig: {
              mapRoutes: {
                style: {
                  justifyItems: 'center',
                  whiteSpace: 'nowrap'
                }
              }
            },
            headers: this.stopsReportHeaders,
            paginationConfig: this.defaultPaginationConfig
          }
        },
        breadcrumbs: this.breadcrumbs
      }
    },

    getOverspeedReportTableConfig () {
      return {
        tableConfig: {
          key: 'overspeed_report',
          tableData: this.reportData,
          ...this.defaultTableGlobalConfig,
          tableConfig: {
            ...this.defaultTableConfig,
            cellsConfig: {
              mapRoutes: {
                style: {
                  justifyItems: 'center',
                  whiteSpace: 'nowrap'
                }
              }
            },
            headers: this.overspeedReportHeaders,
            paginationConfig: this.defaultPaginationConfig
          }
        },
        breadcrumbs: this.breadcrumbs
      }
    },

    getRoadOverspeedReportTableConfig () {
      return {
        tableConfig: {
          key: 'road_overspeed_report',
          tableData: this.reportData,
          ...this.defaultTableGlobalConfig,
          tableConfig: {
            ...this.defaultTableConfig,
            cellsConfig: {
              mapRoutes: {
                style: {
                  justifyItems: 'center',
                  whiteSpace: 'nowrap'
                }
              }
            },
            headers: this.roadOverspeedReportHeaders,
            paginationConfig: this.defaultPaginationConfig
          }
        },
        breadcrumbs: this.breadcrumbs
      }
    },

    getLowVoltageReportTableConfig () {
      return {
        tableConfig: {
          key: 'low_voltage_report',
          tableData: this.reportData,
          ...this.defaultTableGlobalConfig,
          tableConfig: {
            ...this.defaultTableConfig,
            cellsConfig: {
              mapRoutes: {
                style: {
                  justifyItems: 'center',
                  whiteSpace: 'nowrap'
                }
              }
            },
            headers: this.lowVoltageReportHeaders,
            paginationConfig: this.defaultPaginationConfig
          }
        },
        breadcrumbs: this.breadcrumbs
      }
    },

    getGeofenceReportTableConfig () {
      return {
        tableConfig: {
          key: 'geofence_report',
          tableData: this.reportData,
          ...this.defaultTableGlobalConfig,
          tableConfig: {
            ...this.defaultTableConfig,
            cellsConfig: {
              mapRoutes: {
                style: {
                  justifyItems: 'center',
                  whiteSpace: 'nowrap'
                }
              }
            },
            headers: this.geofenceReportHeaders,
            paginationConfig: this.defaultPaginationConfig
          }
        },
        breadcrumbs: this.breadcrumbs
      }
    },

    getIdleVehOpReportTableConfig () {
      return {
        tableConfig: {
          key: 'idle_vehicle_operation_report',
          tableData: this.filtersReportDataByTab,
          summaryData: this.filterTabDataByKey(this.reportSummaryData, true),
          showLoader: this.showLoader,
          ...this.defaultTableGlobalConfig,
          underFiltersCustomComponents: [
            {
              component: this.tableTabsComponent,
              onTabChange: (event) => this.onTabChange(event)
            }
          ],
          tableConfig: {
            ...this.defaultTableConfig,
            headers: this.idleVehOpReportHeaders,
            paginationConfig: this.defaultPaginationConfig
          }
        },
        breadcrumbs: this.breadcrumbs
      }
    },

    getTemperatureReportTableConfig () {
      return {
        tableConfig: {
          key: 'temperature_report',
          tableData: this.filtersReportDataByTab,
          showLoader: this.showLoader,
          ...this.defaultTableGlobalConfig,
          underFiltersCustomComponents: [
            {
              component: this.tableTabsComponent,
              onTabChange: (event) => this.onTabChange(event)
            }
          ],
          tableConfig: {
            ...this.defaultTableConfig,
            cellsConfig: {
              door_opened: {
                custom_display_value: (event) => {
                  return this.$t(`base.${event?.door_opened ? 'open' : 'closed'}`)
                }
              }
            },
            headers: this.temperatureReportHeaders,
            paginationConfig: this.defaultPaginationConfig
          }
        },
        breadcrumbs: this.breadcrumbs
      }
    },

    getExpensesReportTableConfig () {
      return {
        tableConfig: {
          key: 'expenses_report',
          tableData: this.reportData,
          summaryData: [this.reportSummaryData],
          ...this.defaultTableGlobalConfig,
          tableConfig: {
            ...this.defaultTableConfig,
            headers: this.expensesReportHeaders,
            paginationConfig: this.defaultPaginationConfig
          }
        },
        breadcrumbs: this.breadcrumbs
      }
    },

    getPassingThroughLocationReportTableConfig () {
      return {
        tableConfig: {
          key: 'pass_through_location_report',
          tableData: this.reportData,
          ...this.defaultTableGlobalConfig,
          tableConfig: {
            ...this.defaultTableConfig,
            cellsConfig: {
              mapRoutes: {
                style: {
                  justifyItems: 'center',
                  whiteSpace: 'nowrap'
                }
              }
            },
            headers: this.passThLocationReportHeaders,
            paginationConfig: this.defaultPaginationConfig
          }
        },
        breadcrumbs: this.breadcrumbs
      }
    },

    getVehicleSensorsReportTableConfig () {
      const isTimeFormat = this.selectedSensor?.type === 'digital' && this.selectedSensor?.display_format === 'time'

      return {
        tableConfig: {
          key: 'vehicle_sensors_report',
          tableData: this.reportData,
          summaryData: [this.reportSummaryData],
          ...this.defaultTableGlobalConfig,
          tableConfig: {
            ...this.defaultTableConfig,
            headers: this.vehicleSensorsReportHeaders,
            paginationConfig: this.defaultPaginationConfig,
            ...(isTimeFormat
              ? {
                  rowExpansion: {
                    isExpandable: (rowData) => Array.isArray(rowData?.activations) && rowData.activations.length > 0,
                    component: SensorActivationsExpandTable,
                    props: (rowData) => ({
                      activations: rowData.activations,
                      batchReverseGeocode: !!this.batchReverseGeocode,
                      onShowRoute: (activation) => this.showActivationRouteOverview(activation)
                    })
                  }
                }
              : {})
          }
        },
        breadcrumbs: this.breadcrumbs
      }
    },

    getMachineOperationReportTableConfig () {
      return {
        tableConfig: {
          key: 'machine_operation_report',
          tableData: this.reportData,
          summaryData: [this.reportSummaryData],
          ...this.defaultTableGlobalConfig,
          tableConfig: {
            ...this.defaultTableConfig,
            headers: this.machineOperationReportHeaders,
            paginationConfig: this.defaultPaginationConfig
          }
        },
        breadcrumbs: this.breadcrumbs
      }
    },

    getBusinessPrivateDriveReportTableConfig () {
      return {
        tableConfig: {
          key: 'business_private_drive_report',
          tableData: this.filtersReportDataByTab,
          summaryData: this.filterTabDataByKey(this.reportSummaryData, true),
          showLoader: this.showLoader,
          ...this.defaultTableGlobalConfig,
          underFiltersCustomComponents: [
            {
              component: this.tableTabsComponent,
              onTabChange: (event) => this.onTabChange(event)
            }
          ],
          tableConfig: {
            ...this.defaultTableConfig,
            headers: this.businessPrivateDriveReportHeaders,
            paginationConfig: this.defaultPaginationConfig
          }
        },
        breadcrumbs: this.breadcrumbs
      }
    },

    getVehicleAvailabilityReportTableConfig () {
      return {
        tableConfig: {
          key: 'vehicle_availability_report',
          tableData: this.reportData,
          ...this.defaultTableGlobalConfig,
          tableConfig: {
            ...this.defaultTableConfig,
            headers: this.vehicleAvailabilityReportHeaders,
            paginationConfig: this.defaultPaginationConfig
          }
        },
        breadcrumbs: this.breadcrumbs
      }
    },

    getDriversByDayReportTableConfig () {
      return {
        tableConfig: {
          key: 'drivers_day_by_day_report',
          tableData: this.filtersReportDataByTab,
          summaryData: this.filterTabDataByKey(this.reportSummaryData, true),
          showLoader: this.showLoader,
          ...this.defaultTableGlobalConfig,
          underFiltersCustomComponents: [
            {
              component: this.tableTabsComponent,
              onTabChange: (event) => this.onTabChange(event)
            }
          ],
          tableConfig: {
            ...this.defaultTableConfig,
            headers: this.driversDayByDayReportHeaders,
            paginationConfig: this.defaultPaginationConfig
          }
        },
        breadcrumbs: this.breadcrumbs
      }
    },

    getDriversSummaryReportTableConfig () {
      return {
        tableConfig: {
          key: 'drivers_summary_report',
          tableData: this.reportData,
          summaryData: [this.reportSummaryData],
          ...this.defaultTableGlobalConfig,
          tableConfig: {
            ...this.defaultTableConfig,
            headers: this.driversSummaryReportHeaders,
            paginationConfig: this.defaultPaginationConfig
          }
        },
        breadcrumbs: this.breadcrumbs
      }
    },

    getVehicleActivityHistoryReportTableConfig () {
      return {
        tableConfig: {
          key: 'vehicle_activity_history_report',
          tableData: this.reportData,
          ...this.defaultTableGlobalConfig,
          tableConfig: {
            ...this.defaultTableConfig,
            headers: this.vehicleActivityHistoryReportHeaders,
            paginationConfig: this.defaultPaginationConfig
          }
        },
        breadcrumbs: this.breadcrumbs
      }
    },

    getVehicleIncidentViolationsReportTableConfig () {
      return {
        tableConfig: {
          key: 'vehicle_incident_violations_report',
          tableData: this.reportData,
          ...this.defaultTableGlobalConfig,
          tableConfig: {
            ...this.defaultTableConfig,
            headers: this.vehicleIncidentViolationsReportHeaders,
            paginationConfig: this.defaultPaginationConfig
          }
        },
        breadcrumbs: this.breadcrumbs
      }
    },

    getVehicleIncidentDamagesReportTableConfig () {
      return {
        tableConfig: {
          key: 'vehicle_incident_damages_report',
          tableData: this.reportData,
          ...this.defaultTableGlobalConfig,
          tableConfig: {
            ...this.defaultTableConfig,
            headers: this.vehicleIncidentDamagesReportHeaders,
            paginationConfig: this.defaultPaginationConfig
          }
        },
        breadcrumbs: this.breadcrumbs
      }
    },

    getDriversTripsReportTableConfig () {
      return {
        tableConfig: {
          key: 'drivers_trips_report',
          tableData: this.reportData,
          summaryData: [this.reportSummaryData],
          ...this.defaultTableGlobalConfig,
          tableConfig: {
            ...this.defaultTableConfig,
            headers: this.driversTripsReportHeaders,
            paginationConfig: this.defaultPaginationConfig
          }
        },
        breadcrumbs: this.breadcrumbs
      }
    },

    getDriversStopsReportTableConfig () {
      const mapRoutesVisible = !this.activeColumnKeys || this.activeColumnKeys.has('mapRoutes')
      return {
        tableConfig: {
          key: 'drivers_stops_report',
          tableData: this.reportData,
          summaryData: [
            [
              ...this.reportSummaryData,
              ...(mapRoutesVisible ? [' '] : [])
            ]
          ],
          ...this.defaultTableGlobalConfig,
          tableConfig: {
            ...this.defaultTableConfig,
            cellsConfig: {
              mapRoutes: {
                style: {
                  justifyItems: 'center',
                  whiteSpace: 'nowrap'
                }
              }
            },
            headers: this.driversStopsReportHeaders,
            paginationConfig: this.defaultPaginationConfig
          }
        },
        breadcrumbs: this.breadcrumbs
      }
    },

    getDriversViolationsReportTableConfig () {
      return {
        tableConfig: {
          key: 'drivers_violations_report',
          tableData: this.reportData,
          ...this.defaultTableGlobalConfig,
          tableConfig: {
            ...this.defaultTableConfig,
            headers: this.driversViolationsReportHeaders,
            paginationConfig: this.defaultPaginationConfig
          }
        },
        breadcrumbs: this.breadcrumbs
      }
    },

    getDriversDamagesReportTableConfig () {
      return {
        tableConfig: {
          key: 'drivers_damages_report',
          tableData: this.reportData,
          ...this.defaultTableGlobalConfig,
          tableConfig: {
            ...this.defaultTableConfig,
            headers: this.driversDamagesReportHeaders,
            paginationConfig: this.defaultPaginationConfig
          }
        },
        breadcrumbs: this.breadcrumbs
      }
    }
  }
}
